import {
  ChevronDownIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Popover,
  ScrollArea,
  Text,
} from '@radix-ui/themes';
import { CommonChecklist } from 'components/common/checklist';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonTextInput } from 'components/common/form/text';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { t } from 'i18next';
import { IMultiSelectInput } from 'interfaces/forms/multiselect';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { useEffect, useMemo, useState } from 'react';
import './index.scss';

const AUTO_CLEAR_SEARCH = false;

export const CommonMultiSelectInput = (props: IMultiSelectInput) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (props.disabled) {
      setIsOpen(false);
    }
  }, [props.disabled]);

  const groupedOptions = useMemo(() => {
    const output: IOption[] = [];

    const grouped = ArrayHelper.groupBy(
      props.options.filter((o) => !o.invisible),
      'group'
    );

    const keys = Object.keys(grouped);

    if (!props.skipSort) {
      keys.sort();

      if (props.reverseSort) {
        keys.reverse();
      }
    }

    keys.forEach((key) => {
      const group = grouped[key];

      if (!group) {
        return;
      }

      if (group.length === 0) {
        return;
      }

      if (key) {
        output.push({
          label: key,
          value: key,
          group: key,
          isGroup: true,
          disabled: true,
          hideControl: true,
        });
      }

      if (!props.skipSort) {
        group.sort((a, b) => StringHelper.safeSort(a.label, b.label));

        if (props.reverseSort) {
          group.reverse();
        }
      }

      output.push(...group);
    });

    return output;
  }, [props.options, props.skipSort, props.reverseSort]);

  const matchingOptions = useMemo(() => {
    if (!searchTerm) {
      return groupedOptions;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered: IOption[] = [];

    let currentGroup: IOption | null = null;
    let hasItemsInGroup = false;

    groupedOptions.forEach((option) => {
      if (option.isGroup) {
        // Start a new group
        currentGroup = option;
        hasItemsInGroup = false;
      } else {
        const matches = option.label.toLowerCase().includes(searchLower);
        if (matches) {
          // If this is the first matching item in the group, add the group header
          if (currentGroup && !hasItemsInGroup) {
            filtered.push(currentGroup);
          }
          filtered.push(option);
          hasItemsInGroup = true;
        }
      }
    });

    return filtered;
  }, [groupedOptions, searchTerm]);

  const selectedOptions = useMemo(() => {
    return groupedOptions.filter((option) =>
      props.values.includes(option.value)
    );
  }, [groupedOptions, props.values]);

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Popover.Root
        open={isOpen}
        onOpenChange={(open: boolean) => {
          if (props.disabled) {
            setIsOpen(false);
            return;
          }

          setIsOpen(open);

          if (!open && AUTO_CLEAR_SEARCH) {
            setSearchTerm('');
          }
        }}
      >
        <Popover.Trigger>
          <Box
            className="MultiSelectTrigger cursor-pointer"
            onKeyDown={(e) => {
              // Prevent typing but allow navigation keys
              if (
                !['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(
                  e.key
                )
              ) {
                e.preventDefault();
              }
            }}
          >
            <Flex
              className="MultiSelectTriggerContent bg-background"
              justify="between"
              align="center"
              gap="2"
              p="1"
            >
              <Flex
                gap="2"
                wrap="wrap"
                style={{
                  margin: '1px 4px',
                  minHeight: '20px',
                }}
              >
                {selectedOptions.length === 0 && (
                  <Text color={RADIX.COLOR.NEUTRAL}>{props.placeholder}</Text>
                )}

                {selectedOptions.map((option, i) => (
                  <Badge
                    key={i}
                    color={RADIX.COLOR.NEUTRAL}
                    className="MultiSelectSelectedBadge cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();

                      props.onChange?.(
                        props.values.filter((v) => v !== option.value)
                      );
                    }}
                  >
                    {option.label} <Cross2Icon />
                  </Badge>
                ))}
              </Flex>

              <ChevronDownIcon
                color={RADIX.COLOR.NEUTRAL}
                className={`MultiSelectChevron ${isOpen ? 'up' : 'down'}`}
              />
            </Flex>
          </Box>
        </Popover.Trigger>

        <Popover.Content style={{ padding: 0 }}>
          <Card className="bg-background">
            <Flex direction="column" gap="2">
              <Flex gap="2" align="center">
                <Box flexGrow="1">
                  <CommonTextInput
                    id={`${props.id}-search-input`}
                    placeholder={t('common.search').toString()}
                    value={searchTerm}
                    onChange={(value) => {
                      setSearchTerm(value ?? '');
                      props.onSearch?.(value ?? '');
                    }}
                    leftSlot={
                      <MagnifyingGlassIcon color={RADIX.COLOR.NEUTRAL} />
                    }
                  />
                </Box>

                <Button
                  color={RADIX.COLOR.NEUTRAL}
                  variant={RADIX.BUTTON.VARIANT.SECONDARY}
                  disabled={selectedOptions.length === 0}
                  onClick={() => props.onChange?.([])}
                >
                  <Text size="1">Clear All</Text>
                </Button>
              </Flex>

              <ScrollArea
                type="auto"
                scrollbars="vertical"
                style={{ maxHeight: 250 }}
              >
                {matchingOptions.length > 0 ? (
                  <Box pl="1">
                    <CommonChecklist
                      id={`${props.id}-checklist`}
                      options={matchingOptions}
                      values={props.values}
                      onChange={(values) => props.onChange?.(values)}
                    />
                  </Box>
                ) : (
                  <Box>
                    <Text color={RADIX.COLOR.SECONDARY}>
                      {t('common.no-results-found')}
                    </Text>
                  </Box>
                )}
              </ScrollArea>
            </Flex>
          </Card>
        </Popover.Content>
      </Popover.Root>

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};
